import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button, 
  Card, 
  CardActions, 
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  FormHelperText,
  Grid,
  Box,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Company } from '../../../types/UserTypes';
import axios from '../../../../services/ApiService';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { StaticData, CompanyCategory } from '../../../types/DataTypes';

function CompanyEditServiceInfo() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = (location.state as { companyId?: number })?.companyId;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [categories, setCategories] = useState<CompanyCategory[]>([]);
  const [regions, setRegions] = useState<StaticData[]>([]);
  const [sectors, setSectors] = useState<StaticData[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [selectedRegions, setSelectedRegions] = useState<number[]>([]);
  const [selectedSectors, setSelectedSectors] = useState<number[]>([]);
  const [companyData, setCompanyData] = useState<Company>({
    companyId: 0,
    userId: 0,
    agentId: 0,
    firstName: '',
    lastName: '',
    companyName: '',
    agentABN: '',
    email: '',
    contact: '',
    isDefault: false,
    isVerified: false,
    verificationComment: '',
    address: '',
    status: BVTConstants.ACTIVE,
    certId: 0,
    notes: '',
    webLink: '',
    logoId: 0,
    serviceId: 0,
    categoryId: 0,
    regions: [],
    sectors: [],
    bio: '',
    platformFee: false,
    feeAmount: 0,
    frequency: 'MONTHLY',
    createdAt: '',
    updatedAt: '',
    lastLoginAt: '',
  });

  useEffect(() => {
    fetchCompanyData();
    fetchCategories();
    fetchRegions();
    fetchSectors();
  }, []);

  useEffect(() => {
    if (companyData) {
      setLoading(true);
      if (companyData.categoryId) {
        setSelectedCategory(companyData.categoryId);
      }
      
      if (companyData.regions && companyData.regions.length > 0) {
        setSelectedRegions(companyData.regions);
      }
      
      if (companyData.sectors && companyData.sectors.length > 0) {
        setSelectedSectors(companyData.sectors);
      }
      setLoading(false);
    }
  }, [companyData]);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/getCompany", companyId);
      setCompanyData(response.data);
    } catch (error) {
      console.error('Error fetching company data.');
      toast.error("Failed to fetch company data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategories([]);
    setLoading(true);
    try {
      const response = await axios.get('/getCompanyCategories');
      if (Array.isArray(response.data)) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegions = async () => {
    setRegions([]);
    setLoading(true);
    try {
      const response = await axios.post('/getStaticData', BVTConstants.COMPANY_REGION);
      if (Array.isArray(response.data)) {
        setRegions(response.data);
      }
    } catch (error) {
      console.error('Error fetching regions.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    setSectors([]);
    setLoading(true);
    try {
      const response = await axios.post('/getStaticData', BVTConstants.COMPANY_SECTOR);
      if (Array.isArray(response.data)) {
        setSectors(response.data);
      }
    } catch (error) {
      console.error('Error fetching sectors.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCompanyData(prev => ({
      ...prev,
      [name]: value,
      regions: selectedRegions,
      sectors: selectedSectors
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent<number>) => {
    const { value } = event.target;
    setSelectedCategory(value as number);
    setCompanyData(prev => ({
      ...prev,
      categoryId: value as number
    }));
  };

  const handleRegionChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    setSelectedRegions(typeof value === 'string' ? [] : value);
  };

  const handleSectorChange = (event: SelectChangeEvent<number[]>) => {
    const { value } = event.target;
    setSelectedSectors(typeof value === 'string' ? [] : value);
  };

  const handleSubmit = async () => {
    setErrors({});
    let newErrors: { [key: string]: string } = {};

    if (selectedCategory === 0) {
      newErrors.categories = 'Please select a category';
    }
    if (selectedRegions.length === 0) {
      newErrors.regions = 'Please select at least one region';
    }
    if (selectedSectors.length === 0) {
      newErrors.sectors = 'Please select at least one sector';
    }
    if (!companyData.bio?.trim()) {
      newErrors.bio = 'Bio is required';
    } else if (companyData.bio.length > 250) {
      newErrors.bio = 'Bio cannot exceed 250 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const updatedData = {
        ...companyData,
        categoryId: selectedCategory,
        regions: selectedRegions,
        sectors: selectedSectors
      };
      await axios.post("/updateCompanyServiceInfo", updatedData);
      toast.success("Company service info updated successfully");
      navigate(-1);
    } catch (error) {
      console.error('Error updating company service info');
      toast.error("Failed to update company service info");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>BVT Company - Edit</title></Helmet>
      <Stack spacing={3}>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <Card>
            <CardHeader title="Edit Service Profile" />
            <Divider />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item md={12} xs={12}>
                  <FormControl fullWidth error={!!errors.categories}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      input={<OutlinedInput label="Category" />}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.catId} value={category.catId}>
                          {category.catName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.categories && <FormHelperText>{errors.categories}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item md={12} xs={12}>
                  <FormControl fullWidth error={!!errors.regions}>
                    <InputLabel>Regions</InputLabel>
                    <Select
                      multiple
                      value={selectedRegions}
                      onChange={handleRegionChange}
                      input={<OutlinedInput label="Regions" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={regions.find(reg => reg.dataId === value)?.dataValue || value} 
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {regions.map((region) => (
                        <MenuItem key={region.dataId} value={region.dataId}>
                          {region.dataValue}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.regions && <FormHelperText>{errors.regions}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item md={12} xs={12}>
                  <FormControl fullWidth error={!!errors.sectors}>
                    <InputLabel>Sectors</InputLabel>
                    <Select
                      multiple
                      value={selectedSectors}
                      onChange={handleSectorChange}
                      input={<OutlinedInput label="Sectors" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip 
                              key={value} 
                              label={sectors.find(sec => sec.dataId === value)?.dataValue || value} 
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {sectors.map((sector) => (
                        <MenuItem key={sector.dataId} value={sector.dataId}>
                          {sector.dataValue}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.sectors && <FormHelperText>{errors.sectors}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item md={12} xs={12}>
                  <FormControl fullWidth error={!!errors.bio}>
                    <InputLabel>Bio</InputLabel>
                    <OutlinedInput
                      name="bio"
                      value={companyData.bio || ''}
                      onChange={handleChange}
                      label="Bio"
                      multiline
                      rows={4}
                      inputProps={{ maxLength: 250 }}
                    />
                    <FormHelperText>
                      {errors.bio || `${(companyData.bio || '').length}/250`}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button variant="contained" color='error' onClick={() => navigate(-1)}>Cancel</Button>
              <Button variant="contained" type="submit">Update</Button>
            </CardActions>
          </Card>
        </form>
        <LoadingSpinner loading={loading} />
      </Stack>
    </>
  );
}

export default CompanyEditServiceInfo; 